import React, { useEffect, useRef } from 'react'

export type VideoDialogProps = {
  isOpen: boolean
  videoId: string
  title: string
  src: string
  closeLabel: string
  backLabel: string
  onClose: () => void
}

const VideoDialog: React.FC<VideoDialogProps> = ({ isOpen, videoId, title, src, closeLabel, backLabel, onClose }) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const titleId = `video-dialog-${videoId}-title`

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || !isOpen) return

    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousBodyOverflow = document.body.style.overflow

    if (!dialog.open) {
      dialog.showModal()
    }
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      if (dialog.open) {
        dialog.close()
      }
      returnFocusRef.current?.focus()
    }
  }, [isOpen])

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      data-video-id={videoId}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      className="h-dvh max-h-none w-full max-w-none border-0 bg-transparent p-0 text-gray-900 backdrop:bg-black/60 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:w-[min(960px,calc(100vw-2rem))] sm:rounded-xl"
    >
      <div className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-auto sm:rounded-xl">
        <header className="relative grid shrink-0 grid-cols-[auto_1fr] items-center gap-3 border-b border-[#E5E7EB] px-4 py-3 sm:block sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={backLabel}
            className="inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm font-semibold text-[#006045] focus:ring-2 focus:ring-[#006045]/25 focus:outline-none sm:hidden"
          >
            <span aria-hidden="true">&larr;</span>
            <span>{backLabel}</span>
          </button>

          <h2
            id={titleId}
            className="truncate text-center text-lg font-semibold text-[#002C22] sm:pr-12 sm:text-left sm:text-xl"
          >
            {title}
          </h2>

          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="absolute top-1/2 right-4 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-2xl leading-none text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-[#006045]/25 focus:outline-none sm:right-6 sm:inline-flex"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </header>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black sm:bg-white sm:p-6">
          <div className="aspect-video max-h-full w-full max-w-4xl overflow-hidden bg-black sm:rounded-lg">
            <iframe
              key={`${videoId}:${src}`}
              src={src}
              title={title}
              loading="lazy"
              allow="encrypted-media; fullscreen; picture-in-picture; screen-wake-lock;"
              allowFullScreen
              tabIndex={0}
              className="h-full w-full border-0"
            />
          </div>
        </div>
      </div>
    </dialog>
  )
}

export default VideoDialog
